import React from 'react';
import { Link } from 'react-router-dom';
import { animated, useTransition } from '@react-spring/web';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faCalendarCheck } from '@fortawesome/pro-solid-svg-icons/faCalendarCheck';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faGem } from '@fortawesome/pro-solid-svg-icons/faGem';
import { faHandshake } from '@fortawesome/pro-solid-svg-icons/faHandshake';
import { faInfinity } from '@fortawesome/pro-solid-svg-icons/faInfinity';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons/faShieldCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAnimatedRouteState } from '@wildidea/components/AnimatedRoutes/AnimatedRoutes';
import { Card } from '@wildidea/components/Card/Card';
import { useThemeAnimations } from '@wildidea/components/Theme/Theme';

import * as css from './PricingRoute.module.css';

const FEATURES = [
  'Full patient assessment workflow (Scene Survey through Transfer)',
  'Head-to-Toe documentation with interactive body map',
  'SOAP Note generation',
  'Scribe - transcribes the patient interview into observations (included while in preview)',
  'ATMIST satellite message preparation',
  'Vitals tracking and history',
  'PDF export for medical handoffs',
  'Offline-first - works without any signal',
  'English and Spanish interface',
  'HIPAA-compliant data security with on-device encryption',
  'Responsive support and feedback WITH THE DEVELOPER, not an AI. I personally answer all support and feedback emails, usually within an hour.'
];

const PricingRoute: React.FC<{}> = () => {
  const { phase, onTransitionEnd } = useAnimatedRouteState();
  const active = phase === 'enter' || phase === 'active';
  const { fromBelow } = useThemeAnimations('Common');
  const contentTransition = useTransition(active, { ...fromBelow, onRest: onTransitionEnd });
  return contentTransition((style, active) =>
    active ? (
      <animated.div style={style} className={css.layout}>
        <div className={css.pageHeader}>
          <Link to="/" aria-label="Back">
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
          <h1>Pricing</h1>
        </div>

        <section className={css.hero}>
          <h2>Own it. Don't rent it.</h2>
          <div className={css.heroSub}>
            <div>Once you pay, the app is yours.</div>
            <div>Own it forever — and updates are always free.</div>
          </div>
        </section>

        <section className={css.cards}>
          <Card color="paper1" className={css.pricingCard}>
            <div className={css.cardIcon}>
              <FontAwesomeIcon icon={faCalendarCheck} />
            </div>
            <h3>Payment Plan</h3>
            <div className={css.price}>
              <span className={css.amount}>$7.99</span>
              <span className={css.period}>/mo</span>
            </div>
            <div className={css.total}>for 12 months</div>
            <div className={css.thenFree}>
              <FontAwesomeIcon icon={faInfinity} />
              <span>Then own it forever</span>
            </div>
            <p className={css.cardNote}>
              12 payments, then done. Not a subscription&nbsp;— it stops automatically.
            </p>
          </Card>

          <Card color="paper1" className={[css.pricingCard, css.featured].join(' ')} elevated>
            <div className={css.bestValue}>Best Value</div>
            <div className={css.cardIcon}>
              <FontAwesomeIcon icon={faGem} />
            </div>
            <h3>One-Time Purchase</h3>
            <div className={css.price}>
              <span className={css.amount}>$79.99</span>
              <span className={css.period}>once</span>
            </div>
            <div className={css.total}>Save 16%</div>
            <div className={css.thenFree}>
              <FontAwesomeIcon icon={faInfinity} />
              <span>Then own it forever</span>
            </div>
            <p className={css.cardNote}>Pay once, never again. Simple as that.</p>
          </Card>
        </section>

        <section className={css.lifetime}>
          <div className={css.lifetimeIcon}>
            <FontAwesomeIcon icon={faHandshake} />
          </div>
          <h2>About lifetime access</h2>
          <p>
            <strong>Once you've bought Rescue Ally, it's yours.</strong> Everyone who buys lifetime
            access keeps it forever, no matter what. The same app, the same free updates, at no
            additional cost.
          </p>
          <p>
            However, Rescue Ally is a new business, and we may not be able to offer lifetime access
            forever.{' '}
            <strong>
              Please help us keep lifetime access available to future users by buying it now.
            </strong>{' '}
            Each sale supports our business and our mission. If we can make the business viable with
            the lifetime access model, we will never change it.
          </p>
          <p>
            <strong>
              Our promise to you is that we will make it worth way more than the money you spend.
            </strong>{' '}
            We're only just getting started. With your support and feedback, we'll continue to make
            this app the tool every rescuer and adventurer deserves.
          </p>
        </section>

        <section className={css.included}>
          <h2>What's included</h2>
          <p className={css.includedSub}>
            Both options unlock the <strong>full app</strong> immediately:
          </p>
          <ul className={css.featureList}>
            {FEATURES.map((feature) => (
              <li key={feature}>
                <FontAwesomeIcon icon={faCheck} className={css.checkIcon} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className={css.noSurprises}>
          <div className={css.noSurprisesIcon}>
            <FontAwesomeIcon icon={faShieldCheck} />
          </div>
          <h2>No surprises</h2>
          <ul className={css.noSurprisesList}>
            <li>
              <strong>Keep what you pay for</strong> — forever
            </li>
            <li>
              <strong>Updates will always be free</strong>
            </li>
            <li>
              <strong>No ads</strong> — ever
            </li>
            <li>
              <strong>No selling your data</strong> — ever (see our{' '}
              <Link to="/privacy">Privacy Policy</Link>)
            </li>
            <li>
              <strong>Never lose access</strong>
            </li>
            <li>No hidden costs, no bait-and-switch</li>
          </ul>
        </section>

        <footer className={css.footer}>
          <p>
            The monthly plan is a payment plan, not a membership. If you delete your account before
            completing all 12 payments, the remaining balance is still due. We offer full refunds if
            requested within 14 days of your initial purchase. See our{' '}
            <Link to="/terms">Terms of Service</Link> for details.
          </p>
          <p>
            All payments are processed securely through{' '}
            <a href="https://www.stripe.com" target="_blank" rel="noopener noreferrer">
              Stripe
            </a>
            , our payment partner. Stripe supports credit/debit cards and a variety of local payment
            methods depending on your region.
          </p>
          <p>
            Questions? Contact us at{' '}
            <a href="mailto:support@rescueally.org">support@rescueally.org</a>
          </p>
        </footer>
      </animated.div>
    ) : null
  );
};

export default PricingRoute;
